import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import BasicInfoTab from './BasicInfoTab';
import TravelPreferencesTab from './TravelPreferencesTab';
import SavedAddressesTab from './SavedAddressesTab';
import FrequentTravelerTab from './FrequentTravelerTab';
import TravelDocumentsTab from './TravelDocumentsTab';
import MiscTab from './MiscTab';
import EmergencyContactTab from './EmergencyContactTab';
import UrbanrideProfileTab from './UrbanrideProfileTab';
import DateOfBirthSelector from './DateOfBirthSelector';

// Move FormField outside of the component to prevent re-creation on every render
const FormField = ({ id, label, value, onChange, placeholder, required = false, type = "text", className = "" }) => (
  <div className="space-y-2 group">
    <label htmlFor={id} className={`text-sm font-semibold text-slate-700 ${required ? 'after:content-["*"] after:text-red-500 after:ml-1' : ''}`}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className={`w-full h-12 px-4 bg-white border border-slate-200 rounded-xl
        focus:border-violet-400 focus:ring-4 focus:ring-violet-100 focus:shadow-lg focus:shadow-violet-100/50
        hover:border-slate-300 hover:shadow-sm
        transition-all duration-200 ease-out
        placeholder:text-slate-400 ${className}`}
    />
  </div>
);
import AddressAutofill from './AddressAutofill';
import { useParadigm } from '../../contexts/ParadigmContext';

const EditExecutiveForm = ({ executive, onSave, onCancel }) => {
  const { labels } = useParadigm();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    honorific: '',
    company_title: '',
    email: '',
    other_emails: [],
    phone: '',
    mobile: '',
    company: '',
    department: '',
    date_of_birth: '',
    gender: '',
    is_active: true,
    assistant_notes: '',
    address: {
      addressLine1: '',
      city: '',
      state_province: '',
      postalCode: '',
      countryCode: '',
      countryName: ''
    },
    saved_addresses: [], // Array of { id, label, address, is_default, place_id }
    travel_preferences: {
      preferred_airline: '',
      seat_preference: '',
      hotel_chain_preference: '',
      room_type_preference: '',
      car_rental_preference: '',
      dietary_restrictions: '',
      special_requests: ''
    },
    frequent_traveler_programs: [],
    travel_documents: {
      known_traveler_number: '',
      global_entry: false,
      visa_info: ''
    },
    passports: [],
    emergency_contact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    // Urbanride passenger profile IDs for booking reuse
    urbanride_pax_id_test: null,
    urbanride_pax_id_production: null
  });

  const [activeTab, setActiveTab] = useState('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const savedAddressesRef = useRef(null);

  // Initialize form data from executive prop
  useEffect(() => {
    if (executive) {
      setFormData({
        first_name: executive.first_name || '',
        last_name: executive.last_name || '',
        honorific: executive.honorific || executive.title || '',
        company_title: executive.company_title || '',
        email: executive.email || '',
        other_emails: executive.other_emails || [],
        phone: executive.phone || '',
        mobile: executive.mobile || '',
        company: executive.company || '',
        department: executive.department || '',
        date_of_birth: executive.date_of_birth || '',
        gender: executive.gender || '',
        is_active: executive.is_active !== false,
        assistant_notes: executive.assistant_notes || '',
        address: {
          addressLine1: executive.address?.addressLine1 || executive.address_line1 || '',
          city: executive.address?.city || executive.city || '',
          state_province: executive.address?.state_province || executive.state || '',
          postalCode: executive.address?.postalCode || executive.postal_code || '',
          countryCode: executive.address?.countryCode || executive.country || ''
        },
        saved_addresses: executive.saved_addresses || [],
        travel_preferences: {
          // Map backend field names to frontend field names for display
          // Also support legacy frontend field names for data saved before the field name fix
          preferred_airline: executive.travel_preferences?.preferred_airlines ?
            (Array.isArray(executive.travel_preferences.preferred_airlines) ?
              executive.travel_preferences.preferred_airlines.join(', ') :
              executive.travel_preferences.preferred_airlines) :
            (executive.travel_preferences?.preferred_airline || ''),
          seat_preference: executive.travel_preferences?.preferred_seat_type ||
            executive.travel_preferences?.seat_preference || '',
          hotel_chain_preference: executive.travel_preferences?.preferred_hotel_chains ?
            (Array.isArray(executive.travel_preferences.preferred_hotel_chains) ?
              executive.travel_preferences.preferred_hotel_chains.join(', ') :
              executive.travel_preferences.preferred_hotel_chains) :
            (executive.travel_preferences?.hotel_chain_preference || ''),
          room_type_preference: executive.travel_preferences?.preferred_room_type ||
            executive.travel_preferences?.room_type_preference || '',
          car_rental_preference: executive.travel_preferences?.preferred_car_rental_companies ?
            (Array.isArray(executive.travel_preferences.preferred_car_rental_companies) ?
              executive.travel_preferences.preferred_car_rental_companies.join(', ') :
              executive.travel_preferences.preferred_car_rental_companies) :
            (executive.travel_preferences?.car_rental_preference || ''),
          dietary_restrictions: executive.travel_preferences?.meal_preference ||
            executive.travel_preferences?.dietary_restrictions ||
            executive.dietary_restrictions || '',
          special_requests: executive.travel_preferences?.special_requests || ''
        },
        frequent_traveler_programs: executive.frequent_traveler_programs || executive.loyalty_programs || [],
        travel_documents: {
          known_traveler_number: executive.travel_documents?.known_traveler_number || '',
          global_entry: executive.travel_documents?.global_entry || false,
          visa_info: executive.travel_documents?.visa_info || ''
        },
        // Handle migration from old passport_details (single object) to new passports array
        passports: executive.passports?.length > 0
          ? executive.passports
          : (executive.passport_details
              ? [{
                  id: `passport-migrated-${Date.now()}`,
                  first_name: executive.first_name || '',
                  last_name: executive.last_name || '',
                  passport_number: executive.passport_details.passport_number || '',
                  date_of_birth: executive.date_of_birth || '',
                  place_of_birth: executive.passport_details.place_of_birth || '',
                  date_of_issue: executive.passport_details.issue_date || '',
                  date_of_expiration: executive.passport_details.expiry_date || '',
                  authority: executive.passport_details.country_of_issue || '',
                  nationality: executive.passport_details.nationality || '',
                  image_key: null,
                  created_at: new Date().toISOString(),
                  _migrated_from_legacy: true
                }]
              : []),
        emergency_contact: {
          name: executive.emergency_contact?.name || '',
          relationship: executive.emergency_contact?.relationship || '',
          phone: executive.emergency_contact?.phone || '',
          email: executive.emergency_contact?.email || ''
        },
        // Urbanride passenger profile IDs
        urbanride_pax_id_test: executive.urbanride_pax_id_test || null,
        urbanride_pax_id_production: executive.urbanride_pax_id_production || null
      });
    }
  }, [executive]);

  const menuItems = [
    {
      value: "basic",
      label: "Personal Info",
      icon: "👤",
      color: "text-blue-600"
    },
    {
      value: "travel",
      label: "Travel Preferences",
      icon: "✈️",
      color: "text-violet-600"
    },
    {
      value: "saved-addresses",
      label: "Saved Addresses",
      icon: "📍",
      color: "text-purple-600"
    },
    {
      value: "frequent-traveler",
      label: "Loyalty Programs",
      icon: "⭐",
      color: "text-amber-600"
    },
    {
      value: "documents",
      label: "Travel Documents",
      icon: "🛡️",
      color: "text-green-600"
    },
    {
      value: "misc",
      label: "Additional Info",
      icon: "📋",
      color: "text-slate-600"
    },
    {
      value: "emergency",
      label: "Emergency Contact",
      icon: "❤️",
      color: "text-red-600"
    },
    {
      value: "urbanride",
      label: "Urbanride Profile",
      icon: "🚗",
      color: "text-slate-600"
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleAddressChange = (newAddress) => {
    setFormData(prev => ({
      ...prev,
      address: newAddress
    }));
  };

  const handleSavedAddressesChange = (newSavedAddresses) => {
    setFormData(prev => ({
      ...prev,
      saved_addresses: newSavedAddresses
    }));
  };

  const handlePassportsChange = (newPassports) => {
    setFormData(prev => ({
      ...prev,
      passports: newPassports
    }));
  };

  // Handle immediate save for saved addresses (called when user clicks Save Address)
  const handleImmediateAddressSave = async (partialData) => {
    if (!executive?.id) return;

    try {
      const apiBase = process.env.REACT_APP_API_BASE || '/api';

      const headers = {};

      // Send only the saved_addresses field to update
      await axios.put(`${apiBase}/executives/${executive.id}`, partialData, { headers });
    } catch (error) {
      console.error('Failed to save address immediately:', error);
      throw error; // Re-throw so the SavedAddressesTab can handle it
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      // Build the final form data, including any pending address
      let finalFormData = { ...formData };

      // Check for pending address in saved addresses form
      if (savedAddressesRef.current?.savePendingAddress) {
        const pendingAddress = savedAddressesRef.current.savePendingAddress();
        if (pendingAddress) {
          // Add the pending address to saved_addresses
          finalFormData = {
            ...finalFormData,
            saved_addresses: [...(finalFormData.saved_addresses || []), pendingAddress]
          };
        }
      }

      // Transform travel_preferences field names from frontend to backend format
      // Frontend uses different field names than the backend TravelPreferences model
      const travelPrefs = finalFormData.travel_preferences || {};
      finalFormData.travel_preferences = {
        // Convert comma-separated strings to arrays for backend
        preferred_airlines: travelPrefs.preferred_airline
          ? travelPrefs.preferred_airline.split(',').map(s => s.trim()).filter(s => s)
          : [],
        preferred_seat_type: travelPrefs.seat_preference || '',
        preferred_hotel_chains: travelPrefs.hotel_chain_preference
          ? travelPrefs.hotel_chain_preference.split(',').map(s => s.trim()).filter(s => s)
          : [],
        preferred_room_type: travelPrefs.room_type_preference || '',
        preferred_car_rental_companies: travelPrefs.car_rental_preference
          ? travelPrefs.car_rental_preference.split(',').map(s => s.trim()).filter(s => s)
          : [],
        meal_preference: travelPrefs.dietary_restrictions || '',
        special_requests: travelPrefs.special_requests || ''
      };

      // For new executives, executive will be null/undefined, so use null instead of executive.id
      const executiveId = executive ? executive.id : null;
      await onSave(executiveId, finalFormData);
    } catch (error) {
      console.error('Failed to save executive:', error);
      // Set an error state or show user feedback
      setErrors({ general: 'Failed to save ' + labels.executive.toLowerCase() + ': ' + (error.response?.data?.detail || error.message) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full grid lg:grid-cols-12 gap-8 items-start">
      {/* Tab Navigation - Left Side */}
      <div className="lg:col-span-4">
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl shadow-slate-200/50 rounded-3xl sticky top-6 p-5 overflow-hidden">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50/30 via-transparent to-purple-50/30 pointer-events-none" />

          <div className="relative flex flex-col w-full space-y-1.5">
            {menuItems.map(({ value, label, icon, color }) => (
              <button
                key={value}
                onClick={() => setActiveTab(value)}
                className={`relative w-full justify-start text-left p-3.5 rounded-2xl transition-all duration-300 ease-out group
                  ${activeTab === value
                    ? 'bg-gradient-to-r from-violet-500 to-purple-600 shadow-lg shadow-violet-500/25 scale-[1.02]'
                    : 'hover:bg-slate-50/80 hover:shadow-sm active:scale-[0.98]'
                  }`}
              >
                <div className="flex items-center gap-3.5 w-full">
                  <div className={`p-2.5 rounded-xl transition-all duration-300
                    ${activeTab === value
                      ? 'bg-white/20 shadow-inner'
                      : 'bg-slate-100/80 group-hover:bg-white group-hover:shadow-sm'
                    } flex-shrink-0`}>
                    <span className={`text-lg ${activeTab === value ? 'drop-shadow-sm' : ''}`}>{icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold text-sm transition-colors duration-300
                      ${activeTab === value ? 'text-white' : 'text-slate-700 group-hover:text-slate-900'}`}>
                      {label}
                    </div>
                  </div>
                  {/* Active indicator arrow */}
                  <svg
                    className={`w-4 h-4 transition-all duration-300
                      ${activeTab === value
                        ? 'text-white/80 translate-x-0 opacity-100'
                        : 'text-slate-300 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-50'
                      }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Tab Content - Right Side */}
      <div className="lg:col-span-8">
        {activeTab === 'basic' && (
          <BasicInfoTab
            formData={formData}
            handleInputChange={handleInputChange}
            handleAddressChange={handleAddressChange}
            FormField={FormField}
          />
        )}

        {activeTab === 'travel' && (
          <TravelPreferencesTab
            formData={formData}
            handleNestedInputChange={handleNestedInputChange}
            FormField={FormField}
          />
        )}

        {activeTab === 'saved-addresses' && (
          <SavedAddressesTab
            ref={savedAddressesRef}
            formData={formData}
            handleSavedAddressesChange={handleSavedAddressesChange}
            executiveId={executive?.id}
            onImmediateSave={handleImmediateAddressSave}
          />
        )}

        {activeTab === 'frequent-traveler' && (
          <FrequentTravelerTab
            programs={formData.frequent_traveler_programs}
            onUpdate={(programs) => handleInputChange('frequent_traveler_programs', programs)}
            executiveId={executive?.id}
          />
        )}

        {activeTab === 'documents' && (
          <TravelDocumentsTab
            formData={formData}
            handleNestedInputChange={handleNestedInputChange}
            executiveId={executive?.id}
            onPassportsChange={handlePassportsChange}
          />
        )}

        {activeTab === 'misc' && (
          <MiscTab
            formData={formData}
            handleNestedInputChange={handleNestedInputChange}
            FormField={FormField}
          />
        )}

        {activeTab === 'emergency' && (
          <EmergencyContactTab
            formData={formData}
            handleNestedInputChange={handleNestedInputChange}
            FormField={FormField}
          />
        )}

        {activeTab === 'urbanride' && (
          <UrbanrideProfileTab
            urbanridePaxIdTest={formData.urbanride_pax_id_test}
            urbanridePaxIdProduction={formData.urbanride_pax_id_production}
            executiveFirstName={formData.first_name}
            executiveLastName={formData.last_name}
            onUpdateTestProfile={(id) => handleInputChange('urbanride_pax_id_test', id)}
            onUpdateProductionProfile={(id) => handleInputChange('urbanride_pax_id_production', id)}
          />
        )}

        {/* Sticky Footer Actions */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl mt-8 py-6 px-6 -mx-0 border-t border-slate-100 shadow-2xl shadow-slate-200/50 rounded-t-3xl">
          {/* Error Message Display */}
          {errors.general && (
            <div className="mb-4 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-2xl flex items-start gap-3 animate-shake">
              <div className="p-1.5 bg-red-100 rounded-lg">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-red-800 text-sm font-medium">{errors.general}</p>
              </div>
              <button
                type="button"
                onClick={() => setErrors(prev => ({ ...prev, general: null }))}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="px-8 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium
                hover:bg-slate-100 hover:border-slate-300 hover:shadow-sm
                active:scale-[0.98] transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="relative min-w-44 px-8 py-3.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl
                shadow-lg shadow-violet-500/30
                hover:from-violet-500 hover:to-purple-500 hover:shadow-xl hover:shadow-violet-500/40 hover:-translate-y-0.5
                active:scale-[0.98] active:shadow-md
                transition-all duration-200
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
                overflow-hidden group"
            >
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

              {isSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditExecutiveForm;